import type { Language } from '@eformulieren/engine'
import { type KeyboardEvent, type ReactNode, type RefObject, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { BooksIcon } from '../icons/BooksIcon.js'
import { BubbleIcon } from '../icons/BubbleIcon.js'
import { ClockIcon } from '../icons/ClockIcon.js'
import { inLanguage } from '../language/text.js'
import type { SidePanelContext } from './Form.js'
import { CountBadge, countName } from './CountBadge.js'

/**
 * De panelen van het formulier zelf: Bronnen, Opmerkingen en Geschiedenis (ontwerp A van het
 * invulscherm, CitatenPOC `RightDock.tsx`, R-50, R-73, R-97, R-139).
 *
 * **Eén paneelgebied rechts, één paneel tegelijk.** Zoals in de POC (`useActiveDock`): een tweede knop
 * wisselt het paneel, dezelfde knop sluit het. Het adviespaneel van de pagina deelt dat gebied: op een
 * breed formulier staat het er zolang geen ander paneel open is, en sluit u Bronnen, dan staat het er
 * weer. Daaronder gedraagt elk paneel zich als het adviespaneel: een tweede weergave achter zijn knop,
 * met de knop Formulier om terug te gaan (`Form`, `.ef-formulier__weergave`).
 *
 * **Op een telefoon een blad over het hele scherm.** Smaller dan 40rem (dezelfde grens als de
 * containerquery's van het formulier) staat het paneel in een modale `<dialog>`, zoals de POC op een
 * telefoon een modaal blad toont; de rest van de pagina is dan inert en Tab loopt rond binnen het blad.
 * Breder is het een `<aside>` (rol complementary) naast of in plaats van het formulier, zonder focusval,
 * want dan is het geen dialoog. Escape en de knop Sluiten sluiten het in beide gevallen, en de focus gaat
 * terug naar de knop die het opende (WCAG 2.4.3).
 */

export type FormPanelId = 'bronnen' | 'opmerkingen' | 'geschiedenis'

export interface FormPanel {
  readonly id: FormPanelId
  /** De kop van het paneel, zonder teller: "Bronnen". */
  readonly titel: string
  /** Het label van de knop, zonder aantal: "Bronnen". */
  readonly knop: string
  /**
   * Het aantal naast het label, als vak (`CountBadge`), met wat het telt in woorden voor de naam van de
   * knop: `{ aantal: 2, woorden: '2 open' }` geeft "Opmerkingen: 2 open". Zonder teller alleen het label.
   */
  readonly teller?: PanelCount
  readonly render: (context: SidePanelContext) => ReactNode
}

/** Een aantal op een knop, en hoe het in de naam van de knop klinkt. */
export interface PanelCount {
  readonly aantal: number
  readonly woorden: string
}

/** Het label met het aantal als vak, en de naam die erbij hoort ("Opmerkingen: 2 open"). */
export function labelWithCount(label: string, count: PanelCount | undefined): { readonly inhoud: ReactNode; readonly naam: string | undefined } {
  if (count === undefined || count.aantal === 0) return { inhoud: label, naam: undefined }
  return {
    inhoud: (
      <>
        {label}
        <CountBadge>{String(count.aantal)}</CountBadge>
      </>
    ),
    naam: countName(label, [count.woorden]),
  }
}

/** De panelen van het formulier en welk er open is; zie `FormProps.panelen`. */
export interface FormPanels {
  readonly panelen: readonly FormPanel[]
  readonly actief: FormPanelId | undefined
  /** Telt op bij elke opening, zodat de weergave op een smal scherm wisselt en de focus erin gaat. */
  readonly toon: number
  /** Sluiten; zonder `focusTerug: false` gaat de focus terug naar de knop die het paneel opende. */
  readonly onSluit: (options?: { readonly focusTerug?: boolean }) => void
}

const CLOSE: Readonly<Record<Language, string>> = { nl: 'Sluiten', en: 'Close' }

const ICONS: Readonly<Record<FormPanelId, (props: { 'aria-hidden': 'true' }) => ReactNode>> = {
  bronnen: BooksIcon,
  opmerkingen: BubbleIcon,
  geschiedenis: ClockIcon,
}

/** Het DOM-id van het paneel, voor `aria-controls` op zijn knoppen. */
function panelDomId(id: FormPanelId): string {
  return `ef-paneel-${id}`
}

/**
 * De knoppen die een paneel openen en sluiten, met `aria-expanded` (ontwerp A: rechts naast de titel,
 * met een icoon voor het label). Het aantal staat sinds 1 november 2026 niet meer tussen haakjes in de
 * tekst maar als vak naast het label (`CountBadge`), en de naam van de knop zegt het als zin:
 * "Opmerkingen: 2 open". Ze staan twee keer: naast de titel en op een
 * telefoon achter de knop Meer in de kopbalk; de stylesheet toont er één.
 */
export function PanelButtons({
  panelen: panels,
  actief: active,
  taal: language,
  plek: place,
  onKies: onChoose,
}: {
  readonly panelen: readonly FormPanel[]
  readonly actief: FormPanelId | undefined
  readonly taal: Language
  readonly plek: 'kop' | 'meer'
  readonly onKies: (id: FormPanelId) => void
}): ReactNode {
  if (panels.length === 0) return null
  return (
    <div
      className="ef-paneelknoppen"
      data-plek={place}
      role="group"
      aria-label={inLanguage({ nl: 'Hulpmiddelen bij het formulier', en: 'Form tools' }, language)}
    >
      {panels.map((panel) => {
        const Icon = ICONS[panel.id]
        const counted = panel.teller === undefined || panel.teller.aantal === 0
        return (
          <button
            key={panel.id}
            type="button"
            className="ef-paneelknop"
            data-paneelknop={panel.id}
            aria-expanded={active === panel.id}
            aria-controls={panelDomId(panel.id)}
            onClick={() => onChoose(panel.id)}
            {...(counted ? {} : { 'aria-label': countName(panel.knop, [panel.teller?.woorden]) })}
          >
            <Icon aria-hidden="true" />
            <span>{panel.knop}</span>
            {counted ? null : <CountBadge>{String(panel.teller?.aantal)}</CountBadge>}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Of het formulier zo smal is als een telefoon: dezelfde 40rem als de containerquery's in `schil.css`,
 * gemeten aan het formulier zelf en niet aan het venster, zodat de rol van het paneel en de opmaak
 * op dezelfde grens wisselen.
 */
export function usePhoneWidth(root: RefObject<HTMLElement | null>): boolean {
  const [phone, setPhone] = useState(false)
  useLayoutEffect(() => {
    const element = root.current
    if (element === null) return
    const measure = (): void => {
      const rem = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
      setPhone(element.getBoundingClientRect().width <= 40 * rem)
    }
    measure()
    if (typeof ResizeObserver !== 'function') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [root])
  return phone
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Het kader van een paneel: kop, sluitknop en inhoud, als `<aside>` of op een telefoon als modaal blad.
 *
 * De focus gaat bij elke opening naar de kop (`toon`), zodat een schermlezer hoort waar hij is en
 * Tab daarna door het paneel loopt; bij het blad naar dezelfde kop, binnen de dialoog.
 */
export function PanelFrame({
  paneel: panel,
  taal: language,
  blad: sheet,
  toon: show,
  context,
  onSluit: onClose,
}: {
  readonly paneel: FormPanel
  readonly taal: Language
  readonly blad: boolean
  readonly toon: number
  readonly context: SidePanelContext
  readonly onSluit: () => void
}): ReactNode {
  const headerId = useId()
  const heading = useRef<HTMLHeadingElement>(null)
  const dialog = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    if (!sheet) return
    const element = dialog.current
    if (element !== null && !element.open && typeof element.showModal === 'function') element.showModal()
    return () => {
      if (element?.open === true) element.close()
    }
  }, [sheet])

  useEffect(() => {
    heading.current?.focus()
  }, [show, panel.id, sheet])

  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key === 'Escape' && !event.defaultPrevented) {
      event.preventDefault()
      onClose()
      return
    }
    if (!sheet || event.key !== 'Tab' || dialog.current === null) return
    // Tab en Shift+Tab lopen rond binnen het blad, zoals in de deeldialoog.
    const within = Array.from(dialog.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((element) => element.offsetParent !== null)
    const first = within[0]
    const last = within.at(-1)
    if (first === undefined || last === undefined) return
    if (event.shiftKey && (document.activeElement === first || document.activeElement === heading.current)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const body = (
    <>
      <div className="ef-paneel__kop">
        <h2 id={headerId} ref={heading} tabIndex={-1} className="ef-paneel__titel">
          {panel.titel}
        </h2>
        <button type="button" className="ef-paneel__sluit" aria-label={`${CLOSE[language]}: ${panel.titel}`} onClick={onClose}>
          {CLOSE[language]}
        </button>
      </div>
      <div className="ef-paneel__inhoud">{panel.render(context)}</div>
    </>
  )

  if (sheet) {
    return (
      <dialog
        ref={dialog}
        id={panelDomId(panel.id)}
        className="ef-paneel ef-paneelblad"
        data-paneel={panel.id}
        aria-labelledby={headerId}
        lang={language}
        onKeyDown={onKeyDown}
        onCancel={(event) => {
          // Escape: React houdt bij of het blad open is, dus de browser sluit het niet zelf.
          event.preventDefault()
          onClose()
        }}
      >
        {body}
      </dialog>
    )
  }
  return (
    <aside id={panelDomId(panel.id)} className="ef-paneel" data-paneel={panel.id} aria-labelledby={headerId} lang={language} onKeyDown={onKeyDown}>
      {body}
    </aside>
  )
}
